// =============================================================================
// TOKENS DE TIPOGRAFÍA - SISTEMA DE DISEÑO TADMIN
// =============================================================================
// Tokens de tipografía centralizados para mantener consistencia
// y facilitar el mantenimiento del sistema de diseño

// Familias de fuentes
export const fontFamilies = {
  // Fuente principal del sistema
  primary: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    'Segoe UI',
    'Roboto',
    'Oxygen',
    'Ubuntu',
    'Cantarell',
    'sans-serif'
  ].join(', '),

  // Fuente secundaria (para títulos especiales)
  secondary: [
    'Poppins',
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    'sans-serif'
  ].join(', '),

  // Fuente monoespaciada (para código)
  mono: [
    'JetBrains Mono',
    'Fira Code',
    'Monaco',
    'Consolas',
    'Liberation Mono',
    'Courier New',
    'monospace'
  ].join(', '),

  // Fuente para números (tabular)
  numeric: [
    'Inter',
    'system-ui',
    '-apple-system',
    'sans-serif'
  ].join(', '),
};

// Pesos de fuente
export const fontWeights = {
  thin: 100,
  extraLight: 200,
  light: 300,
  normal: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
  extraBold: 800,
  black: 900,
};

// Tamaños de fuente (escala modular basada en 1.25)
export const fontSizes = {
  xs: '0.75rem',    // 12px
  sm: '0.875rem',   // 14px
  base: '1rem',     // 16px
  lg: '1.125rem',   // 18px
  xl: '1.25rem',    // 20px
  '2xl': '1.5rem',  // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem',  // 36px
  '5xl': '3rem',     // 48px
  '6xl': '3.75rem',  // 60px
  '7xl': '4.5rem',   // 72px
  '8xl': '6rem',     // 96px
  '9xl': '8rem',     // 128px
};

// Alturas de línea
export const lineHeights = {
  none: 1,
  tight: 1.25,
  snug: 1.375,
  normal: 1.5,
  relaxed: 1.625,
  loose: 2,
  // Específicas por tamaño
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
};

// Espaciado de letras
export const letterSpacings = {
  tighter: '-0.05em',
  tight: '-0.025em',
  normal: '0em',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em',
};

// Estilos de texto predefinidos
export const textStyles = {
  // Títulos principales
  h1: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes['4xl'],
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.tight,
    letterSpacing: letterSpacings.tight,
  },

  h2: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes['3xl'],
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.tight,
    letterSpacing: letterSpacings.tight,
  },

  h3: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes['2xl'],
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.snug,
    letterSpacing: letterSpacings.normal,
  },

  h4: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.xl,
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.snug,
    letterSpacing: letterSpacings.normal,
  },

  h5: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.lg,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  h6: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.base,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  // Texto de cuerpo
  body1: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.base,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.relaxed,
    letterSpacing: letterSpacings.normal,
  },

  body2: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  // Subtítulos
  subtitle1: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.lg,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  subtitle2: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.base,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.wide,
  },

  // Texto pequeño
  caption: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.wide,
  },

  overline: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.widest,
    textTransform: 'uppercase',
  },

  // Botones
  button: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.none,
    letterSpacing: letterSpacings.wide,
    textTransform: 'none',
  },

  buttonLarge: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.base,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.none,
    letterSpacing: letterSpacings.wide,
  },

  buttonSmall: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.none,
    letterSpacing: letterSpacings.wider,
  },

  // Etiquetas de formulario
  label: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  // Texto de ayuda
  helper: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  // Código
  code: {
    fontFamily: fontFamilies.mono,
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  codeBlock: {
    fontFamily: fontFamilies.mono,
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.relaxed,
    letterSpacing: letterSpacings.normal,
  },

  // Números (para tablas, métricas)
  numeric: {
    fontFamily: fontFamilies.numeric,
    fontSize: fontSizes.base,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
    fontVariantNumeric: 'tabular-nums',
  },

  numericLarge: {
    fontFamily: fontFamilies.numeric,
    fontSize: fontSizes.xl,
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.none,
    letterSpacing: letterSpacings.tight,
    fontVariantNumeric: 'tabular-nums',
  },

  // Texto de navegación
  nav: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  // Texto de breadcrumb
  breadcrumb: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  // Texto de tabla
  tableHeader: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.wider,
    textTransform: 'uppercase',
  },

  tableCell: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  // Texto de notificación/toast
  notification: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacings.normal,
  },

  // Texto de tooltip
  tooltip: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.normal,
    lineHeight: lineHeights.snug,
    letterSpacing: letterSpacings.normal,
  },
};

// Utilidades para responsive typography
export const responsiveTextStyles = {
  // Títulos que se adaptan al tamaño de pantalla
  heroTitle: {
    fontFamily: fontFamilies.primary,
    fontWeight: fontWeights.bold,
    lineHeight: lineHeights.tight,
    letterSpacing: letterSpacings.tight,
    fontSize: {
      base: fontSizes['3xl'],
      md: fontSizes['4xl'],
      lg: fontSizes['5xl'],
      xl: fontSizes['6xl'],
    },
  },

  pageTitle: {
    fontFamily: fontFamilies.primary,
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.tight,
    letterSpacing: letterSpacings.tight,
    fontSize: {
      base: fontSizes['2xl'],
      md: fontSizes['3xl'],
      lg: fontSizes['4xl'],
    },
  },

  sectionTitle: {
    fontFamily: fontFamilies.primary,
    fontWeight: fontWeights.semiBold,
    lineHeight: lineHeights.snug,
    letterSpacing: letterSpacings.normal,
    fontSize: {
      base: fontSizes.xl,
      md: fontSizes['2xl'],
    },
  },
};

// Utilidades de texto
export const textUtilities = {
  // Truncar texto
  truncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // Texto en múltiples líneas con ellipsis
  clamp: (lines = 2) => ({
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  }),

  // Texto seleccionable
  selectable: {
    userSelect: 'text',
    WebkitUserSelect: 'text',
    MozUserSelect: 'text',
    msUserSelect: 'text',
  },

  // Texto no seleccionable
  unselectable: {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
  },

  // Texto con espaciado de palabras
  wordSpacing: {
    normal: { wordSpacing: 'normal' },
    wide: { wordSpacing: '0.1em' },
    wider: { wordSpacing: '0.2em' },
  },

  // Texto con break de palabras
  wordBreak: {
    normal: { wordBreak: 'normal' },
    words: { wordBreak: 'break-word' },
    all: { wordBreak: 'break-all' },
  },
};

// Función para generar estilos de texto responsivos
export const createResponsiveTextStyle = (baseStyle, breakpoints = {}) => {
  const style = { ...baseStyle };
  
  Object.entries(breakpoints).forEach(([breakpoint, overrides]) => {
    if (breakpoint === 'base') {
      Object.assign(style, overrides);
    } else {
      // Aquí se pueden agregar media queries específicas
      // Esto dependería del sistema de breakpoints usado
    }
  });
  
  return style;
};

// Función para combinar estilos de texto
export const combineTextStyles = (...styles) => {
  return Object.assign({}, ...styles);
};

// Exportar todo
const typographyTokens = {
  fontFamilies,
  fontWeights,
  fontSizes,
  lineHeights,
  letterSpacings,
  textStyles,
  responsiveTextStyles,
  textUtilities,
  createResponsiveTextStyle,
  combineTextStyles,
};
export default typographyTokens;